import React, { useEffect, useState } from "react";

import {
  Code,
  ExpandIcon as Expand,
  Label,
  LoadingIcon as Loading,
  Modal,
  Typography
} from "@packages/ds";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";

import { CopyButton, ButtonWrapper } from "ds/Button";
import Select from "ds/Select";
import useDecodedParams from "Hooks/useDecodedParams";
import { useLocalForage } from "Hooks/useLocalForage";
import {
  configDataSelector,
  configLoadingSelector,
  registryDataSelector,
  registryLoadingSelector
} from "Reducers/project/wizard";

import * as S from "../Steps.styles";

const Services = ({ filterImageType }) => {
  const intl = useIntl();
  const {
    value: selectedServices,
    setValue: setSelectedServices,
    isLoading
  } = useLocalForage("wizardconfig");

  const { projectId } = useDecodedParams();

  const [modalOpen, setModal] = useState(false);
  const [services, setServices] = useState();
  const [template, setTemplate] = useState("");
  const [list, setList] = useState([]);

  const config = useSelector(configDataSelector);
  const configLoading = useSelector(configLoadingSelector);
  const registry = useSelector(registryDataSelector);
  const registryLoading = useSelector(registryLoadingSelector);

  useEffect(() => {
    if (registryLoading || !registry) return;

    const keys = Object.keys(registry) || [];
    const values = keys.reduce((acc, cu) => {
      if (registry[cu]?.name && !registry[cu].runtime && filterImageType(cu)) {
        acc.push({ value: cu, label: registry[cu].name });
      }
      return acc;
    }, []);
    setList(values);
  }, [Object.keys(registry).length]);

  useEffect(() => {
    if (isLoading || configLoading) return;
    let listSelected = [];
    if (selectedServices && selectedServices[projectId]) {
      listSelected = selectedServices[projectId];
    }
    setServices(listSelected);
  }, [isLoading, configLoading]);

  useEffect(() => {
    if (!services) return;
    updateTemplate();
    setSelectedServices({
      ...selectedServices,
      ...{ [projectId]: services }
    });
  }, [services]);

  const updateTemplate = () => {
    const serviceTpl = services.reduce((acc, cu) => {
      const service = window.atob(config[cu.value].service);
      acc += `${service}\n\n`;
      return acc;
    }, "");
    setTemplate(serviceTpl);
  };

  const exampleFile = intl.formatMessage({
    id: "example_file"
  });

  const labelText = intl.formatMessage(
    { id: "file.services.yaml" },
    {
      prependText: exampleFile
    }
  );

  return (
    <>
      <S.Wrapper>
        {!registryLoading && !configLoading && list.length > 0 ? (
          <S.SelectWrapper>
            <Select
              id="wizard-services-selection"
              placeholder={intl.formatMessage({ id: "select_services" })}
              onChange={options => setServices(options)}
              value={services}
              options={list}
              isMulti={true}
              closeMenuOnSelect={false}
            />
          </S.SelectWrapper>
        ) : (
          <Loading />
        )}

        <S.FileExampleLabelWrapper>
          <Label>{labelText}</Label>
          <S.ExpandButton onClick={() => setModal(!modalOpen)}>
            <Expand />
          </S.ExpandButton>
        </S.FileExampleLabelWrapper>

        <S.CodeBlock
          id="services-yaml"
          singleLine={false}
          isCopyable={false}
          separateCopyButton={true}
        >
          {template
            ? template
            : intl.formatMessage({ id: "yaml_service_default" })}
        </S.CodeBlock>
      </S.Wrapper>

      <Modal
        isOpen={modalOpen}
        size="large"
        onOpenChange={() => setModal(false)}
        handleCancel={() => setModal(false)}
      >
        <Typography.Title tag="h3">{labelText}</Typography.Title>

        <Code.Block>
          {template
            ? template
            : intl.formatMessage({ id: "yaml_service_default" })}
        </Code.Block>

        <ButtonWrapper justifyContent="end" spacing="modal">
          {template.length && <CopyButton text={template} />}
        </ButtonWrapper>
      </Modal>
    </>
  );
};

Services.propTypes = {
  filterImageType: PropTypes.func
};

export default Services;
